import { getUrlWithHash } from "@jti/fe-common";
import type { HistoryPointsItemType } from "@jti/store/slices/loyaltyCoreSlice";
import type { Translate } from "@jti/ui/providers/useTranslations";
import { replaceStringPlaceholder } from "@jti/ui/utils/replaceStringPlaceholder";

import type { EventMessagesType } from "../HistoryTable";

const getEventDisplayName = (
  __: Translate,
  item: HistoryPointsItemType,
  messages?: EventMessagesType,
  isCouponDisplayedInNewLineOnMobile?: boolean,
  profilePageUrl?: string
) => {
  if (!messages || !item) {
    return "";
  }

  const translatedEvent = item.event ? __(item.event) : "";
  let displayName;
  const placeholdersConfig = [
    {
      placeholder: "event",
      value: translatedEvent,
    },
    {
      placeholder: "coupon",
      value:
        item.coupon_type === "url"
          ? `<a target="_blank" href='${item.coupon}'>${messages.externalCouponLinkLabel}</a>`
          : __(item?.coupon || ""),
    },
    {
      placeholder: "orderId",
      value: item.order_id || "",
    },
    {
      placeholder: "orderUrl",
      value: item.order_id
        ? `<a href='${getUrlWithHash(`#/orders/${item.order_id}`, profilePageUrl)}'>#${item.order_id}</a>`
        : "",
    },
  ];

  const pointsRedemptionMessage = item.order_id ? messages.pointsRedemptionMessage : translatedEvent;

  const REWARD_STATUS_CANCELED = "canceled";
  const isRedemptionFailed = item.issued_reward_status === REWARD_STATUS_CANCELED;

  const getRedemptionMessageVariants = () => {
    if (isRedemptionFailed && messages.redemptionFailedMessage) {
      if (isCouponDisplayedInNewLineOnMobile) {
        return `<div>${translatedEvent}</div> <div>${messages.redemptionFailedMessage}</div>`;
      }
      return `${translatedEvent} ${messages.redemptionFailedMessage}`;
    }

    if (isCouponDisplayedInNewLineOnMobile) {
      const couponMessageStandardRedemption = item.coupon ? `${messages.couponLabel || ""} ${item?.coupon || ""}` : "";
      const pinBlock = item.coupon_pin ? `<div>${messages.pinLabel || ""} ${item.coupon_pin}</div>` : "";

      const couponBlock =
        item.coupon_type === "url" || item.coupon_type === "url_with_pin"
          ? `<a target="_blank" href='${item.coupon}'>${messages.externalCouponLinkLabel}</a>`
          : couponMessageStandardRedemption;
      return `<div>${translatedEvent}</div> ${pinBlock} ${couponBlock}`;
    }

    return replaceStringPlaceholder(
      item.coupon ? messages.pointsCouponRedemptionMessage : pointsRedemptionMessage,
      placeholdersConfig
    );
  };

  switch (item.type) {
    case "PointsWereAdded":
      displayName = item.order_id
        ? replaceStringPlaceholder(messages.pointsOrderAddedMessage, placeholdersConfig)
        : translatedEvent || messages.defaultPointsAddedMessage;
      break;
    case "PointsWereSpent":
      displayName = translatedEvent ? getRedemptionMessageVariants() : messages.defaultPointsSpentMessage;
      break;
    case "PointsTransferHasBeenExpired":
      displayName = replaceStringPlaceholder(messages.pointsExpirationMessage, placeholdersConfig);
      break;
    case "PointsTransferHasBeenCanceled":
      displayName = replaceStringPlaceholder(messages.pointsCancellationMessage, placeholdersConfig);
      break;
    case "PointsWereRefunded":
      displayName = messages.pointsRefundMessage
        ? replaceStringPlaceholder(messages.pointsRefundMessage, placeholdersConfig)
        : __("loyalty.order_refund_points_message", [item.event]);
      break;
    default:
      displayName = translatedEvent;
      break;
  }

  return displayName;
};

export default getEventDisplayName;
